// src/pages/Customers.js
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiPlus, FiX, FiTrash2, FiEdit, FiSave, FiHome } from 'react-icons/fi'; // Removed FiRefreshCw
import { AuthContext } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

const AddCommunityModal = ({ isOpen, onClose, onAddCommunity }) => {
  const [newCommunity, setNewCommunity] = useState({
    name: '',
    status: 'Active',
    address: { street: '', city: '', state: '', zip: '' }
  });
  const [isLookingUp, setIsLookingUp] = useState(false);
  const overlayMousedownTarget = useRef(null);
  const { showAlert } = useModal(); 

  useEffect(() => {
    if (isOpen) {
      setNewCommunity({
        name: '',
        status: 'Active',
        address: { street: '', city: '', state: '', zip: '' }
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && isOpen) {
            e.preventDefault();
            onClose();
        }
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
   
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleMouseDown = (e) => {
    overlayMousedownTarget.current = e.target;
  };
  const handleMouseUp = (e) => {
    if (overlayMousedownTarget.current === e.target && e.target === e.currentTarget) {
      onClose();
    }
    overlayMousedownTarget.current = null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (['street', 'city', 'state', 'zip'].includes(name)) {
        setNewCommunity(prev => ({ ...prev, address: { ...prev.address, [name]: value } }));
    } else {
        setNewCommunity(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddressLookup = async () => {
    const { street, city, state } = newCommunity.address;
    if (!street || !city || !state) {
      showAlert('Please fill in the Street, City, and State fields before looking up the zip code.', 'Missing Info');
      return;
    }
    setIsLookingUp(true);
    try {
      const response = await fetch(`/api/maps/lookup?street=${encodeURIComponent(street)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`);
      if (!response.ok) {
        throw new Error('Address not found');
      }
      const data = await response.json();
      if (data.zip) {
        setNewCommunity(prev => ({
          ...prev,
          address: { ...prev.address, zip: data.zip }
        }));
      }
    } catch (error) {
      showAlert('Could not automatically find the zip code. Please enter it manually.', 'Lookup Failed');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newCommunity.name || !newCommunity.address.street) {
        showAlert('Please fill out at least the Name and Street Address fields.', 'Validation Error');
        return;
    }
    onAddCommunity(newCommunity);
    onClose();
  };

  return (
    <div className="modal-overlay" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
      <div className="modal-content">
        <div className="modal-header">
          <h3>Add New Community</h3>
          <button onClick={onClose} className="modal-close-btn"><FiX /></button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group"><label>Name</label><input type="text" name="name" value={newCommunity.name} onChange={handleChange} required /></div>
          <div className="form-group"><label>Street Address</label><input type="text" name="street" value={newCommunity.address.street} onChange={handleChange} /></div>
          <div className="form-group"><label>City</label><input type="text" name="city" value={newCommunity.address.city} onChange={handleChange} /></div>
          <div className="form-group"><label>State</label><input type="text" name="state" value={newCommunity.address.state} onChange={handleChange} /></div>
          <div className="form-group">
            <label>Zip Code</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input type="text" name="zip" value={newCommunity.address.zip} onChange={handleChange} style={{ flexGrow: 1 }}/>
              <button type="button" className="btn btn-secondary" onClick={handleAddressLookup} disabled={isLookingUp}>
                <FiSearch size={16} />
              </button>
            </div>
          </div>
          <div className="form-group"><label>Status</label><select name="status" value={newCommunity.status} onChange={handleChange}><option>Active</option><option>Inactive</option></select></div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary">Save Community</button></div>
        </form>
      </div>
    </div>
  );
};

const CommunityCard = ({ community, isEditing, isSelected, onSelect, onViewDetails, style }) => {
    const handleCardClick = () => {
        if (isEditing) {
            onSelect(community._id);
        } else {
            onViewDetails(community._id);
        }
    };

    const cardClasses = `community-card ${isEditing ? 'editing' : ''} ${isSelected ? 'selected' : ''}`;
    const displayCity = community.address?.city || 'N/A';

    return (
        <div className={cardClasses} onClick={handleCardClick} style={style}>
            {isEditing && (
                <div className="card-checkbox-wrapper" onClick={(e) => { e.stopPropagation(); onSelect(community._id); }}>
                    <input type="checkbox" className="custom-checkbox" checked={isSelected} readOnly />
                </div>
            )}
            <div className="community-card-icon"><FiHome size={32} /></div>
            <h3 className="community-card-name">{community.name}</h3>
            <p className="community-card-city">{displayCity}</p>
            <div className="community-card-footer" style={{ marginTop: '0.75rem' }}>
                <span className={`status-badge status-${community.status.toLowerCase()}`}>{community.status}</span>
            </div>
        </div>
    );
};

const Customers = ({ communities, onAddCommunity, onDeleteCommunity, onDeleteMultipleCommunities }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { showConfirm } = useModal(); 
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: 'All Statuses' });
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const canManageCommunities = useMemo(() => 
    user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
    [user]
  );

  useEffect(() => {
      if (location.state?.action === 'newCommunity' && canManageCommunities) {
          setModalOpen(true);
          navigate(location.pathname, { replace: true, state: {} });
      }
  }, [location.state, canManageCommunities, navigate]);

  useEffect(() => {
    let result = communities;

    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(cust =>
        cust.name.toLowerCase().includes(lowercasedTerm)
      );
    }
  
    if (filters.status !== 'All Statuses') {
      result = result.filter(cust => cust.status === filters.status);
    }
    setFilteredCustomers(result);
    setSelectedIds([]);
  }, [searchTerm, filters, communities]);

  const handleFilterChange = (e) => setFilters({ status: e.target.value });
  
  const handleViewDetails = (communityId) => {
    navigate(`/customers/${communityId}`);
  };

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? filteredCustomers.map(c => c._id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };
  
  const handleDeleteSelected = () => {
      showConfirm(
          `Are you sure you want to delete ${selectedIds.length} selected communities? This action cannot be undone.`,
          () => {
              onDeleteMultipleCommunities(selectedIds);
              setSelectedIds([]);
          },
          'Delete Communities',
          true 
      );
  }

  const isAllSelected = selectedIds.length > 0 && selectedIds.length === filteredCustomers.length;

  return (
    <>
      <AddCommunityModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onAddCommunity={onAddCommunity}
      />
      <div className="page-header">
        <h2 className="page-title">Communities</h2>
        {canManageCommunities && (
          <div className="page-actions">
            {isEditing ? (
              <button className="btn btn-secondary" onClick={() => { setIsEditing(false); setSelectedIds([]); }}>
                <FiSave size={16} className="btn-icon" /> Done Editing
              </button>
            ) : (
              <button className="btn btn-secondary" onClick={() => setIsEditing(true)}>
                <FiEdit size={16} className="btn-icon" /> Edit List
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
              <FiPlus size={16} className="btn-icon" /> Add Community
            </button>
          </div>
        )}
      </div>

      {selectedIds.length > 0 ? (
        <div className="table-controls bulk-actions-bar">
          <div className="bulk-actions-info">
                <span>{selectedIds.length}</span> item{selectedIds.length > 1 ? 's' : ''} selected
            </div>
            <div className="bulk-actions-buttons">
                <button className="btn btn-danger" onClick={handleDeleteSelected}>
                    <FiTrash2 className="btn-icon" /> Delete Selected
                </button>
            </div>
        </div>
      ) : (
          <div className="table-controls">
              <div className="search-input-wrapper">
                <FiSearch className="search-icon" />
                <input type="text" placeholder="Search Communities by Name..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <div className="filters-group">
                <div className="filter-wrapper">
                    <label htmlFor="status-filter" className="filter-label">Status</label>
                    <select id="status-filter" className="filter-select" value={filters.status} onChange={handleFilterChange}>
                    <option>All Statuses</option><option>Active</option><option>Inactive</option>
                    </select>
                </div>
              </div>
              {/* Refresh button removed from here */}
          </div>
      )}
      
      {isEditing && (
         <div className="table-controls" style={{ borderTop: 'none', paddingTop: '0.5rem' }}>
            <div className="select-all-wrapper">
                <input type="checkbox" id="select-all-communities" className="custom-checkbox" onChange={handleSelectAll} checked={isAllSelected} />
                <label htmlFor="select-all-communities">Select all ({filteredCustomers.length})</label>
            </div>
        </div>
      )}
      
      {filteredCustomers.length > 0 ? (
          <div className="community-grid-container">
          {filteredCustomers.map((cust, index) => (
            <CommunityCard
                key={cust._id}
                community={cust}
                isEditing={isEditing}
                isSelected={selectedIds.includes(cust._id)}
                onSelect={handleSelectOne}
                onViewDetails={handleViewDetails}
                style={{ animationDelay: `${index * 0.05}s` }} 
              />
          ))}
        </div>
      ) : (
        <div className="no-tasks-message" style={{textAlign: 'center', padding: '3rem 1rem', fontStyle: 'italic', color: 'var(--gray-500)'}}>
          <p>No communities match the current filters.</p>
        </div>
      )}
    </>
  );
};

export default Customers;